import React, { useState, useEffect, useRef } from 'react';
import {
  Bot,
  Play,
  RotateCw,
  Clock,
  CheckCircle2,
  AlertCircle,
  AlertTriangle,
  History,
  Shield,
  Layers,
  FileText,
  Filter,
  Flame,
  Info,
  Calendar,
  ToggleLeft,
  ToggleRight,
  Bell,
  Coins,
  Cpu,
  HelpCircle,
  Zap,
  Radio,
} from 'lucide-react';
import Swal from 'sweetalert2';
import {
  getTenderAgentStatus,
  triggerTenderAgent,
  toggleTenderAgentSchedule,
} from '../../../services/tenderAgentApi';
import { usePermissions } from '../../../utils/usePermissions';

export default function TenderControlPanel({ onTendersUpdated, onOpenHistory, onOpenAlerts, unreadAlertsCount = 0 }) {
  const { typeUser } = usePermissions();
  const isAdmin = typeUser === 'admin';

  const [agentStatus, setAgentStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);

  // Form parameters for manual trigger
  const [mode, setMode] = useState('DRY_RUN');
  const [limit, setLimit] = useState(20);

  const pollingRef = useRef(null);

  useEffect(() => {
    fetchStatus();
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, []);

  // Polling automatique lorsque le statut est EN_COURS
  useEffect(() => {
    if (agentStatus?.status === 'EN_COURS') {
      if (!pollingRef.current) {
        pollingRef.current = setInterval(() => {
          fetchStatus(true);
        }, 3000);
      }
    } else {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    }
  }, [agentStatus?.status]);

  const fetchStatus = async (isBackground = false) => {
    try {
      if (!isBackground) setLoading(true);
      const res = await getTenderAgentStatus();
      if (res?.success && res.data) {
        const prevStatus = agentStatus?.status;
        setAgentStatus(res.data);

        // Si une collecte vient de se terminer, rafraîchir les opportunités
        if (prevStatus === 'EN_COURS' && res.data.status !== 'EN_COURS') {
          if (onTendersUpdated) onTendersUpdated();
        }
      }
    } catch (err) {
      console.error('Erreur récupération statut Tender Agent:', err);
    } finally {
      if (!isBackground) setLoading(false);
    }
  };

  const handleManualTrigger = async () => {
    if (!isAdmin) {
      Swal.fire({
        icon: 'error',
        title: 'Accès restreint',
        text: 'Seul un administrateur est habilité à déclencher une collecte des marchés publics.',
        confirmButtonColor: '#f59e0b',
      });
      return;
    }

    // Confirmation spéciale pour le mode INGEST (écritures en base de données)
    let confirmed = false;
    if (mode === 'INGEST') {
      const confirmIngest = await Swal.fire({
        title: 'Confirmer l\'ingestion en base de données ?',
        html: `
          <div style="text-align: left; font-size: 0.95rem; line-height: 1.55; color: #334155;">
            <p style="margin-bottom: 8px;">
              <strong>Mode sélectionné :</strong> <span style="color: #dc2626; font-weight: 700;">INGEST (Production)</span>
            </p>
            <p style="margin-bottom: 8px;">Cette opération va exécuter en tâche de fond :</p>
            <ul style="padding-left: 20px; margin-bottom: 12px;">
              <li>L'exploration active du portail TUNEPS (volume max : ${limit} marchés)</li>
              <li>L'analyse sémantique et technique par le modèle IA</li>
              <li>La qualification et le stockage des opportunités en base de données</li>
              <li>La génération des alertes internes (échéances imminentes, reports)</li>
            </ul>
            <div style="background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 10px; font-size: 0.85rem; color: #991b1b;">
              🔒 <strong>Confidentialité :</strong> Les opportunités restent strictement privées dans le back-office Sastra Energy.
            </div>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Oui, lancer l\'ingestion',
        cancelButtonText: 'Annuler',
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#64748b',
      });

      if (!confirmIngest.isConfirmed) return;
      confirmed = true;
    } else {
      const confirmResult = await Swal.fire({
        title: 'Lancer la collecte des marchés ?',
        text: `Mode : ${mode} — Limite : ${limit} marchés. L'opération s'exécute en tâche de fond.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Lancer',
        cancelButtonText: 'Annuler',
        confirmButtonColor: '#f59e0b',
        cancelButtonColor: '#64748b',
      });

      if (!confirmResult.isConfirmed) return;
    }

    try {
      setTriggering(true);
      const res = await triggerTenderAgent({
        mode,
        limit: Number(limit),
        confirmed,
      });

      if (res?.success) {
        Swal.fire({
          icon: 'success',
          title: 'Collecte démarrée',
          text: res.message || 'Le Tender Agent explore les marchés publics en tâche de fond.',
          timer: 3000,
          showConfirmButton: false,
        });
        fetchStatus();
      } else {
        throw new Error(res?.message || 'Erreur lors du déclenchement');
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Échec du lancement',
        text: err.message || 'Impossible de contacter le moteur IA.',
        confirmButtonColor: '#f59e0b',
      });
    } finally {
      setTriggering(false);
    }
  };

  const handleToggleSchedule = async () => {
    if (!isAdmin) return;
    const currentState = agentStatus?.scheduleEnabled ?? false;
    const nextState = !currentState;

    const result = await Swal.fire({
      title: nextState ? 'Activer la collecte automatique ?' : 'Désactiver la collecte automatique ?',
      text: nextState
        ? 'Le Tender Agent explorera automatiquement TUNEPS chaque matin à 06:00 (Africa/Tunis).'
        : 'Les collectes automatiques programmées seront suspendues jusqu\'à réactivation.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: nextState ? 'Activer' : 'Désactiver',
      cancelButtonText: 'Annuler',
      confirmButtonColor: nextState ? '#10b981' : '#dc2626',
      cancelButtonColor: '#64748b',
    });

    if (!result.isConfirmed) return;

    try {
      const res = await toggleTenderAgentSchedule(nextState);
      if (res?.success) {
        Swal.fire({
          icon: 'success',
          title: 'Planification mise à jour',
          text: `Collecte automatique ${nextState ? 'activée' : 'désactivée'}.`,
          timer: 2000,
          showConfirmButton: false,
        });
        fetchStatus();
      }
    } catch (err) {
      Swal.fire({
        icon: 'error',
        title: 'Erreur',
        text: err.message || 'Impossible de modifier la planification.',
        confirmButtonColor: '#f59e0b',
      });
    }
  };

  const isRunning = agentStatus?.status === 'EN_COURS' || agentStatus?.status === 'RUNNING' || Boolean(agentStatus?.isRunning || agentStatus?.is_running);
  const lastRunData = agentStatus?.lastRun || agentStatus?.last_run || null;
  const scheduleActive = Boolean(agentStatus?.scheduleEnabled ?? agentStatus?.schedule_enabled);

  return (
    <div className="ao-agent-panel">
      {/* ─── Barre Supérieure d'Identité & Contrôle ─── */}
      <div className="ao-agent-topbar">
        <div className="ao-agent-identity">
          <div className={`ao-agent-avatar ${isRunning ? 'running' : ''}`}>
            <Bot size={24} />
          </div>
          <div>
            <div className="ao-agent-heading">
              <h2 className="ao-agent-title">Tender Agent IA — Veille Marchés Publics</h2>
              <span
                className={`ao-agent-badge ${
                  isRunning
                    ? 'running'
                    : agentStatus?.status === 'SUCCES' || agentStatus?.status === 'SUCCESS' || agentStatus?.status === 'COMPLETED'
                    ? 'success'
                    : agentStatus?.status === 'ERREUR' || agentStatus?.status === 'FAILED'
                    ? 'error'
                    : 'idle'
                }`}
              >
                <span className="ao-agent-dot" />
                {isRunning
                  ? 'EN COURS DE COLLECTE'
                  : agentStatus?.status === 'SUCCES' || agentStatus?.status === 'SUCCESS' || agentStatus?.status === 'COMPLETED'
                  ? 'OPÉRATIONNEL'
                  : agentStatus?.status === 'ERREUR' || agentStatus?.status === 'FAILED'
                  ? 'ERREUR DÉTECTÉE'
                  : 'PRÊT / EN ATTENTE'}
              </span>
            </div>
            <p className="ao-agent-subtitle">
              Portail TUNEPS (Appels d'Offres, Consultations, Projets) • Détection large & scoring sémantique multilingue (FR/AR)
            </p>
          </div>
        </div>

        {/* Actions Supérieures Droite */}
        <div className="ao-agent-actions">
          {/* Bouton Rafraîchir statut */}
          <button
            type="button"
            className="ao-agent-btn-icon"
            onClick={() => fetchStatus()}
            disabled={loading}
            title="Rafraîchir l'état de l'agent"
          >
            <RotateCw size={15} className={loading ? 'animate-spin' : ''} />
          </button>

          {/* Bouton Historique des runs */}
          <button
            type="button"
            className="ao-agent-btn-outline"
            onClick={onOpenHistory}
            title="Voir l'historique complet des exécutions et des coûts"
          >
            <History size={15} className="text-amber-500" />
            <span>Historique ({lastRunData ? 'Actif' : 'Vide'})</span>
          </button>

          {/* Bouton Planification automatique */}
          {isAdmin && (
            <button
              type="button"
              className={`ao-agent-btn-schedule ${scheduleActive ? 'active' : 'suspended'}`}
              onClick={handleToggleSchedule}
              title="Activer ou désactiver la collecte quotidienne automatique à 06:00"
            >
              <Calendar size={15} />
              <span>Auto 06h00 : {scheduleActive ? 'ACTIF' : 'SUSPENDU'}</span>
            </button>
          )}
        </div>
      </div>

      {/* ─── Grille Principale : Télémétrie, KPIs & Déclencheur ─── */}
      <div className="ao-agent-main-grid">
        {/* Colonne Gauche : État & Verrouillage Système */}
        <div className="ao-agent-info-card">
          <div className="ao-agent-info-row">
            <span className="ao-agent-info-label">Planification :</span>
            <span className="ao-agent-info-val">
              {scheduleActive ? (
                <span className="text-emerald-600 font-semibold flex items-center">
                  <CheckCircle2 size={13} className="mr-1" /> Quotidienne à 06:00
                </span>
              ) : (
                <span className="text-gray-500 flex items-center">
                  <Clock size={13} className="mr-1" /> Manuelle uniquement
                </span>
              )}
            </span>
          </div>

          <div className="ao-agent-info-row">
            <span className="ao-agent-info-label">Dernière collecte :</span>
            <span className="ao-agent-info-val text-gray-800 font-medium">
              {(lastRunData?.completedAt || lastRunData?.completed_at)
                ? new Date(lastRunData.completedAt || lastRunData.completed_at).toLocaleString('fr-FR', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : 'Aucune exécution récente'}
            </span>
          </div>

          <div className="ao-agent-info-row">
            <span className="ao-agent-info-label">Verrou distribué :</span>
            <span className="ao-agent-info-val">
              <code className="ao-code-pill">sastra:tender_agent</code>
            </span>
          </div>

          <div className="ao-agent-info-row">
            <span className="ao-agent-info-label">Sources surveillées :</span>
            <div className="ao-agent-sources-list">
              <span className="ao-source-chip">TUNEPS A.O.</span>
              <span className="ao-source-chip">Consultations</span>
              <span className="ao-source-chip">Projets</span>
            </div>
          </div>
        </div>

        {/* Colonne Centrale : Grille des KPIs de Télémétrie */}
        <div className="ao-agent-kpi-grid">
          {/* Découverts au dernier run */}
          <div className="ao-agent-kpi-box blue">
            <div className="ao-agent-kpi-title">Marchés Découverts</div>
            <div className="ao-agent-kpi-val">{lastRunData?.totalDiscovered ?? lastRunData?.total_discovered ?? 0}</div>
            <div className="ao-agent-kpi-sub">Dernier scan TUNEPS</div>
          </div>

          {/* Qualifiés & Ingérés */}
          <div className="ao-agent-kpi-box green">
            <div className="ao-agent-kpi-title">Opportunités Retenues</div>
            <div className="ao-agent-kpi-val">
              {lastRunData?.totalIngested && lastRunData.totalIngested > 0
                ? lastRunData.totalIngested
                : (lastRunData?.totalFiltered ?? lastRunData?.total_filtered ?? 0)}
            </div>
            <div className="ao-agent-kpi-sub">Score IA pertinent (≥ 40)</div>
          </div>

          {/* Consommation Tokens */}
          <div className="ao-agent-kpi-box purple">
            <div className="ao-agent-kpi-title">Tokens LLM</div>
            <div className="ao-agent-kpi-val">
              {((lastRunData?.tokensConsumed ?? lastRunData?.tokens_consumed ?? 0)).toLocaleString()}
            </div>
            <div className="ao-agent-kpi-sub">Analyse sémantique Gemini</div>
          </div>

          {/* Coût estimé */}
          <div className="ao-agent-kpi-box amber">
            <div className="ao-agent-kpi-title">Coût Estimé</div>
            <div className="ao-agent-kpi-val">
              ${Number(lastRunData?.estimatedCostUsd ?? lastRunData?.estimated_cost_usd ?? 0).toFixed(4)}
            </div>
            <div className="ao-agent-kpi-sub">USD par collecte</div>
          </div>

          {/* Filtrés lexicalement */}
          <div className="ao-agent-kpi-box slate">
            <div className="ao-agent-kpi-title">Filtrés Hors-Sujet</div>
            <div className="ao-agent-kpi-val">{lastRunData?.totalRejected ?? lastRunData?.total_rejected ?? lastRunData?.totalFiltered ?? lastRunData?.total_filtered ?? 0}</div>
            <div className="ao-agent-kpi-sub">Économie de tokens ~85%</div>
          </div>

          {/* Durée du run */}
          <div className="ao-agent-kpi-box rose">
            <div className="ao-agent-kpi-title">Durée d'Analyse</div>
            <div className="ao-agent-kpi-val">
              {(lastRunData?.durationSeconds ?? lastRunData?.duration_seconds)
                ? `${Number(lastRunData.durationSeconds ?? lastRunData.duration_seconds).toFixed(1)}s`
                : '—'}
            </div>
            <div className="ao-agent-kpi-sub">Scan asynchrone Celery</div>
          </div>
        </div>

        {/* Colonne Droite : Déclenchement Manuel & Paramétrage */}
        <div className="ao-agent-trigger-card">
          <div className="ao-agent-trigger-title">
            <Zap size={16} className="text-amber-500" />
            <span>Déclencheur Manuel</span>
          </div>

          <div className="ao-agent-form-group">
            <label className="ao-agent-form-label" htmlFor="ao-mode-select">
              Mode d'exécution :
            </label>
            <select
              id="ao-mode-select"
              className="ao-agent-form-select"
              value={mode}
              onChange={(e) => setMode(e.target.value)}
              disabled={isRunning || triggering}
            >
              <option value="DISCOVERY">DISCOVERY — Inventaire TUNEPS passif</option>
              <option value="DRY_RUN">DRY_RUN — Analyse IA (sans persistance DB)</option>
              <option value="INGEST">INGEST — Production & Persistance DB</option>
            </select>
          </div>

          <div className="ao-agent-form-group">
            <label className="ao-agent-form-label" htmlFor="ao-limit-select">
              Plafond de marchés :
            </label>
            <select
              id="ao-limit-select"
              className="ao-agent-form-select"
              value={limit}
              onChange={(e) => setLimit(Number(e.target.value))}
              disabled={isRunning || triggering}
            >
              <option value={10}>10 marchés</option>
              <option value={20}>20 marchés (Recommandé)</option>
              <option value={50}>50 marchés</option>
              <option value={100}>100 marchés</option>
            </select>
          </div>

          <button
            type="button"
            className={`ao-agent-btn-submit ${mode === 'INGEST' ? 'danger' : 'primary'}`}
            onClick={handleManualTrigger}
            disabled={isRunning || triggering || !isAdmin}
            title={!isAdmin ? 'Action réservée aux administrateurs' : 'Lancer immédiatement la collecte'}
          >
            {triggering || isRunning ? (
              <>
                <RotateCw size={15} className="animate-spin" />
                <span>Collecte en cours...</span>
              </>
            ) : (
              <>
                <Play size={15} />
                <span>Lancer la collecte ({mode})</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
